import { SignerWalletAdapterProps } from "@solana/wallet-adapter-base";
import { Connection, Keypair, PublicKey, Signer, Transaction } from "@solana/web3.js";

export function verifySolanaClientOrThrow() {
  if (window.solanaClient === undefined) {
    throw new Error("Error - Solana Client not found");
  }
  if (!window.solanaClient.connected) {
    console.warn("Warning - Solana Wallet not connected even though the client is present");
    window.solanaClient.connect();
    if (!window.solanaClient.connected) {
      throw new Error("Error - Not able to connect to the Solana Wallet");
    }
  }
  return window.solanaClient;
}

export const configureAndSendCurrentTransaction = async (
  transaction: Transaction,
  connection: Connection,
  feePayer: PublicKey,
  signTransaction: SignerWalletAdapterProps["signTransaction"]
) => {
  verifySolanaClientOrThrow();
  const blockHash = await connection.getLatestBlockhash();
  transaction.feePayer = feePayer;
  transaction.recentBlockhash = blockHash.blockhash;
  const signed = await signTransaction(transaction);
  const signature = await connection.sendRawTransaction(signed.serialize());
  await connection.confirmTransaction({
    blockhash: blockHash.blockhash,
    lastValidBlockHeight: blockHash.lastValidBlockHeight,
    signature,
  });
  return signature;
};

export const getPhantomProvider = () => {
  if ("phantom" in window) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const provider = window.phantom?.solana;

    if (provider?.isPhantom) {
      return provider;
    }
  }
  window.open("https://phantom.app/", "_blank");
};

/* Debugging function to send a transaction with Phantom Wallet
 * - Only work for Phantom Wallet
 * @param tx: Transaction - the transaction to send
 * @param connection: Connection - the connection to the Solana network
 * @param errorFromCode: ErrorFromCodeLookup | null - a function to translate error codes to human readable errors
 */
export async function sendWithPhantomAndDebugTransaction(
  tx: Transaction,
  connection: Connection,
  errorFromCode: ErrorFromCodeLookup | null = null,
  signer = window.solanaClient.publicKey,
  extraSigners: Keypair[] = []
) {
  const provider = getPhantomProvider();
  const blockHash = await connection.getLatestBlockhash();
  tx.feePayer = signer;
  tx.recentBlockhash = blockHash.blockhash;
  for (const signer of extraSigners) {
    tx.sign(signer);
  }
  const signedTransaction = await provider.signTransaction(tx);
  try {
    const signature = await connection.sendRawTransaction(signedTransaction.serialize());
    return signature;
  } catch (error) {
    console.error("[ERROR] While sending transaction: ", error);
    if (errorFromCode) {
      console.log("Following is the translated error of the previous error");
      console.error(processError(error.message, errorFromCode));
    }
    throw error;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ErrorFromCodeLookup = (code: number) => any;

export const processError = (errorMessage: string, errorFromCode: ErrorFromCodeLookup) => {
  const customErrorExpression = /.*custom program error: 0x(?<errorNumber>[0-9abcdef]+)/;
  // Extract the error number from the error message
  const match = customErrorExpression.exec(errorMessage);
  const errorNumberFound = match?.groups?.errorNumber;
  if (!errorNumberFound) {
    return errorMessage;
  }
  // errorNumberFound is a base16 string
  const errorNumber = parseInt(errorNumberFound, 16);
  return errorFromCode(errorNumber);
};
